import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/config';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionKicker, SectionHeading, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, FileText, Users2, Landmark, Scale, HandCoins } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const CATEGORY_ICONS = {
  donations: HandCoins,
  expenses: FileText,
  candidate_declarations: Users2,
  governance_rules: Landmark,
  annual_reports: FileText,
  conflicts: Scale,
};

export default function TransparencyPage() {
  const { t } = useLang();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/transparency`);
        if (!c) setCategories(res.data.categories || []);
      } catch (err) {
        void err;
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, []);

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-14 sm:py-16">
          <SectionKicker>{t('nav.transparency')}</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220]">
            {t('transparency.title')}
          </h1>
          <div className="mt-4 max-w-3xl">
            <p className="text-lg text-[#062B5C] font-medium leading-snug">{t('transparency.lede_line1')}</p>
            <p className="text-lg text-[#138A4B] font-medium leading-snug">{t('transparency.lede_line2')}</p>
          </div>
          <p className="mt-4 max-w-3xl text-[#5B6B82] leading-relaxed">{t('transparency.description')}</p>
        </Container>
      </section>

      <section className="py-14">
        <Container>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 w-full" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.key] || ShieldCheck;
                return (
                  <Card key={cat.key} className="border-[#D7E1EF] bg-white" data-testid="transparency-category-card">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="font-display font-semibold text-[#0B1220]">{cat.title}</div>
                          <div className="text-xs text-[#5B6B82]">{cat.description}</div>
                        </div>
                      </div>
                      <div className="mt-5 overflow-hidden rounded-lg border border-[#D7E1EF]">
                        <Table data-testid="transparency-table">
                          <TableHeader className="bg-[#F5F8FC]">
                            <TableRow>
                              {Object.keys(cat.records?.[0] || {}).map((h) => (
                                <TableHead key={h} className="text-[#062B5C] capitalize">{h.replace(/_/g, ' ')}</TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(cat.records || []).map((row, i) => (
                              <TableRow key={i}>
                                {Object.keys(cat.records[0]).map((h) => (
                                  <TableCell key={h} className="text-sm text-[#0B1220]">{String(row[h])}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}
